import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import UsersService from '../../../../services/UsersService';
import UserLookupObject from '../../../../models/UserLookupObject';
import ReadOnlyField from './../ReadOnly';
import { useValidation, FieldProps } from './../ValidationContext';
import { LoadingIndicator, Typeahead, TypeaheadItem } from '@emorg-prd/standard-react';
import { TextPassage } from '../../components/TextPassage';
import { TextLink } from '../../components/TextLinkNew';

interface UserLookupProps extends FieldProps {
	id?: string;
	label: string;
	placeholder?: string;
	note?: string;
	noteLink?: string;
	isEmployee?: boolean;
	isAppAdminEmployee?: boolean;
	isSubscriptionOwner?: boolean;
	isAWSAccount?: boolean;
	isAWSAccountOwner?: boolean;
	disabled?: boolean;
	onChange?: (user: UserLookupObject | null) => void;
	className?: string;
}
interface CacheEntry {
	results: UserLookupObject[];
	timestamp: number;
}

const UserLookup = (props: UserLookupProps) => {
	const usersService = new UsersService();
	var debounceTimer: NodeJS.Timeout | null = null;
	var abortController: AbortController | null = null;
	const cache: Map<string, CacheEntry> = new Map();
	const DEBOUNCE_DELAY = 300; // 300ms debounce
	const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

	const [results, setResults] = useState<UserLookupObject[]>([]);
	const [inputText, setInputText] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const prevValueRef = useRef(props.value);
	const { isValid, nonValidMessage } = useValidation(props.value, props.validation);

	useEffect(() => {
		resetInputText(props.value);
	}, []);

	useEffect(() => {
		if (prevValueRef.current !== props.value) {
			resetInputText(props.value);
			prevValueRef.current = props.value;
		}
	}, [props.value]);

	useEffect(() => {
		return () => {
			if (debounceTimer) {
				clearTimeout(debounceTimer);
			}
			if (abortController) {
				abortController.abort();
			}
		};
	}, []);

	const resetInputText = (newValue) => {
		setInputText(newValue ?? '');
		setResults([]);
	};

	const getCacheKey = (searchText: string): string => {
		const lookupType = props.isSubscriptionOwner
			? 'subsOwner'
			: props.isEmployee
				? 'employee'
				: props.isAppAdminEmployee
					? 'appAdminEmployee'
					: props.isAWSAccount
						? `aws_${props.isAWSAccountOwner}`
						: 'default';

		return `${lookupType}_${searchText.toLowerCase()}`;
	};

	const getCachedResults = (cacheKey: string): UserLookupObject[] | null => {
		const cached = cache.get(cacheKey);
		if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
			return cached.results;
		}
		if (cached) {
			cache.delete(cacheKey);
		}
		return null;
	};

	const setCachedResults = (cacheKey: string, results: UserLookupObject[]): void => {
		cache.set(cacheKey, {
			results,
			timestamp: Date.now(),
		});
	};

	const performUserSearch = async (searchText: string): Promise<void> => {
		const cacheKey = getCacheKey(searchText);
		const cachedResults = getCachedResults(cacheKey);
		if (cachedResults) {
			setResults(cachedResults);
			setLoading(false);
			return;
		}

		if (abortController) {
			abortController.abort();
		}

		abortController = new AbortController();
		setLoading(true);
		try {
			let response: UserLookupObject[];

			if (props.isSubscriptionOwner) {
				response = await usersService.subsOwnerUserLookup(searchText, abortController.signal);
			} else if (props.isEmployee) {
				response = await usersService.employeeUserLookup(searchText, abortController.signal);
			} else if (props.isAppAdminEmployee) {
				response = await usersService.AppAdminEmployeeUserLookup(searchText, abortController.signal);
			} else if (props.isAWSAccount) {
				response = await usersService.accsOwnerOrCustodianUserLookup(
					searchText,
					props.isAWSAccountOwner,
					abortController.signal
				);
			} else {
				response = await usersService.userLookup(searchText, abortController.signal);
			}

			if (!abortController.signal.aborted) {
				setCachedResults(cacheKey, response);
				setResults(response);
				setLoading(false);
			}
		} catch (error) {
			if (!abortController?.signal.aborted) {
				if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
					console.error('User lookup failed:', error);
				}
				setResults([]);
				setLoading(false);
			} else {
				console.log('User search request cancelled for:', searchText);
			}
		}
	};

	const handleTextChange = async (e) => {
		const inputText = e.target.value;
		resetInputText(inputText);
		const searchText = inputText.trim();
		props.onChange(null);

		if (debounceTimer) {
			clearTimeout(debounceTimer);
		}

		if (searchText?.length > 2) {
			debounceTimer = setTimeout(() => {
				performUserSearch(searchText);
			}, DEBOUNCE_DELAY);
		} else {
			if (abortController) {
				abortController.abort();
			}
			setResults([]);
			setLoading(false);
		}
	};

	const renderedResults =
		results.length !== 0 || inputText?.length < 3 || loading ? (
			results.map((user) => (
				<TypeaheadItem value={user.id} key={user.id}>
					<React.Fragment>
						<strong>{`${user.userPrincipalName}`}</strong>&nbsp;
						<span>{`(${user.displayName})`}</span>
					</React.Fragment>
				</TypeaheadItem>
			))
		) : (
			<TypeaheadItem value={'0'}>
				<React.Fragment aria-disabled>
					<i>No results found.</i>
				</React.Fragment>
			</TypeaheadItem>
		);

	return !props.disabled ? (
		<>
			<Typeahead
				onChange={handleTextChange}
				id={props.id}
				label={props.label}
				note={nonValidMessage || (!props.noteLink ? props.note : null)}
				clickHandler={(newValue) => {
					const obj = results.find((o) => o.id === newValue);
					setInputText(obj?.userPrincipalName);
					props.onChange(obj);
				}}
				optionalClass={!isValid ? 'em-has-error' : ''}
				placeholder={props.placeholder}
				value={inputText}
			>
				{loading ? <LoadingIndicator size="s" /> : renderedResults}
			</Typeahead>
			{props.noteLink ? (
				<TextPassage>
					<p className="em-c-field__note" style={!isValid ? { color: '#ca6a72' } : null}>
						{props.note}
						<TextLink href={props.noteLink} external>
							More info...
						</TextLink>
					</p>
				</TextPassage>
			) : null}
		</>
	) : (
		<ReadOnlyField label={props.label} text={inputText} />
	);
};

export default UserLookup;
